import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Card,
  Col,
  Descriptions,
  Divider,
  Empty,
  InputNumber,
  Row,
  Segmented,
  Select,
  Space,
  Spin,
  Statistic,
  Typography,
} from "antd";
import {
  basicAttackDamage,
  computeStats,
  DEFAULT_PIVOT,
  defenseSummary,
  excludeSkillBoundItems,
  MODES,
  modeModifier,
  SLOTS,
} from "./calc";

const SLOT_LABELS = { Weapon: '무기', Chest: '옷', Head: '머리', Arm: '팔', Leg: '다리' };
const GRADE_ORDER = ['Mythic', 'Legend', 'Epic', 'Rare', 'Uncommon', 'Common'];
const GRADE_LABELS = { Mythic: '초월', Legend: '전설', Epic: '영웅', Rare: '희귀', Uncommon: '고급', Common: '일반' };
const GRADE_COLORS = { Mythic: '#e74c3c', Legend: '#f1c40f', Epic: '#9b59b6', Rare: '#3498db', Uncommon: '#2ecc71', Common: '#95a5a6' };
const DEFAULT_GRADES = ['Mythic', 'Legend', 'Epic'];

const pct = (v, digits = 1) => `${((v || 0) * 100).toFixed(digits)}%`;
const num = (v, digits = 1) => (v || 0).toFixed(digits);

function emptyBuild() {
  return { characterCode: null, weaponType: null, level: 20, masteryLevel: 20, adaptiveTo: 'auto', items: SLOTS.map(() => null) };
}

function CharacterLabel({ name }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <img src={`/img/character/${name}.png`} alt="" style={{ width: 20, height: 20, borderRadius: '50%', backgroundColor: '#d6d6d6' }}
        onError={(e) => { e.target.style.display = 'none'; }} />
      {name}
    </span>
  );
}

function ItemLabel({ item }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <img src={`/img/item/${item.code}.png`} alt="" style={{ width: 22, height: 22, objectFit: 'contain', borderBottom: `2px solid ${GRADE_COLORS[item.grade] || '#ccc'}` }}
        onError={(e) => { e.target.style.display = 'none'; }} />
      {item.name}
    </span>
  );
}

function BuildPanel({ title, meta, build, onChange, grades }) {
  const character = meta.characters.find((c) => c.code === build.characterCode);

  const characterOptions = useMemo(() => meta.characters.map((c) => ({
    value: c.code, label: <CharacterLabel name={c.name} />, search: c.name,
  })), [meta.characters]);

  const itemOptionsBySlot = useMemo(() => {
    const result = {};
    for (const slot of SLOTS) {
      result[slot] = meta.items
        .filter((item) => item.slot === slot)
        .filter((item) => grades.includes(item.grade))
        .filter((item) => slot !== 'Weapon' || item.weapon_type === build.weaponType)
        .sort((a, b) => GRADE_ORDER.indexOf(a.grade) - GRADE_ORDER.indexOf(b.grade) || a.name.localeCompare(b.name))
        .map((item) => ({ value: item.code, label: <ItemLabel item={item} />, search: item.name }));
    }
    return result;
  }, [meta.items, grades, build.weaponType]);

  const selectCharacter = (code) => {
    const next = meta.characters.find((c) => c.code === code);
    onChange({
      ...build,
      characterCode: code,
      weaponType: next?.weapons?.[0]?.type || null,
      items: SLOTS.map(() => null),
    });
  };

  const selectWeapon = (type) => {
    const items = [...build.items];
    items[0] = null; // 무기 타입이 바뀌면 무기 칸 초기화
    onChange({ ...build, weaponType: type, items });
  };

  const selectItem = (slotIndex, code) => {
    const items = [...build.items];
    items[slotIndex] = code ? meta.items.find((i) => i.code === code) : null;
    onChange({ ...build, items });
  };

  const filterOption = (input, option) => option.search.toLowerCase().includes(input.toLowerCase());

  return (
    <Card size="small" title={title}>
      <Space direction="vertical" style={{ width: '100%' }}>
        <Select showSearch placeholder="캐릭터" style={{ width: '100%' }} value={build.characterCode}
          options={characterOptions} filterOption={filterOption} onChange={selectCharacter} />
        <Select placeholder="무기" style={{ width: '100%' }} value={build.weaponType} disabled={!character}
          options={(character?.weapons || []).map((w) => ({ value: w.type, label: w.name }))} onChange={selectWeapon} />
        <Row gutter={8}>
          <Col span={12}>
            <InputNumber min={1} max={20} value={build.level} addonBefore="레벨" style={{ width: '100%' }}
              onChange={(v) => onChange({ ...build, level: v || 1 })} />
          </Col>
          <Col span={12}>
            <InputNumber min={0} max={20} value={build.masteryLevel} addonBefore="숙련도" style={{ width: '100%' }}
              onChange={(v) => onChange({ ...build, masteryLevel: v || 0 })} />
          </Col>
        </Row>
        <Space wrap>
          <Typography.Text type="secondary">적응형 능력치</Typography.Text>
          <Segmented size="small" value={build.adaptiveTo}
            options={[{ label: '자동', value: 'auto' }, { label: '공격력', value: 'attack' }, { label: '스킬 증폭', value: 'skill' }]}
            onChange={(v) => onChange({ ...build, adaptiveTo: v })} />
        </Space>
        {SLOTS.map((slot, index) => (
          <Select key={slot} allowClear showSearch placeholder={SLOT_LABELS[slot]} style={{ width: '100%' }}
            disabled={!character || (slot === 'Weapon' && !build.weaponType)}
            value={build.items[index]?.code ?? null}
            options={itemOptionsBySlot[slot]} filterOption={filterOption}
            onChange={(code) => selectItem(index, code)} />
        ))}
      </Space>
    </Card>
  );
}

function StatsCard({ title, stats }) {
  if (!stats) {
    return <Card size="small" title={title}><Empty description="캐릭터를 선택하세요" image={Empty.PRESENTED_IMAGE_SIMPLE} /></Card>;
  }
  const def = defenseSummary(stats);
  return (
    <Card size="small" title={title}>
      <Descriptions size="small" column={{ xs: 1, sm: 2 }} bordered>
        <Descriptions.Item label="공격력">{num(stats.attackPower)}</Descriptions.Item>
        <Descriptions.Item label="스킬 증폭">{num(stats.skillAmp)}</Descriptions.Item>
        <Descriptions.Item label="치명타 확률">{pct(stats.criticalStrikeChance)}</Descriptions.Item>
        <Descriptions.Item label="치명타 피해">+{pct(0.65 + stats.criticalStrikeDamage)}</Descriptions.Item>
        <Descriptions.Item label="방어 관통">{pct(stats.penetrationDefenseRatio)} / {num(stats.penetrationDefense, 0)}</Descriptions.Item>
        <Descriptions.Item label="기본 공격 증폭">{pct(stats.increaseBasicAttackDamageRatio)}</Descriptions.Item>
        <Descriptions.Item label="공격 속도 증가">{pct(stats.attackSpeedRatio)}</Descriptions.Item>
        <Descriptions.Item label="쿨다운 감소">{pct(stats.cooldownReduction)}</Descriptions.Item>
        <Descriptions.Item label="체력">{num(stats.maxHp, 0)}</Descriptions.Item>
        <Descriptions.Item label="방어력">{num(stats.defense)}</Descriptions.Item>
        <Descriptions.Item label="체력 재생">{num(stats.hpRegen, 2)}</Descriptions.Item>
        <Descriptions.Item label="방어력 피해 감소">{pct(def.defenseReduction)}</Descriptions.Item>
        <Descriptions.Item label="유효 체력">{num(def.effectiveHp, 0)}</Descriptions.Item>
        <Descriptions.Item label="기본 공격 피해 감소">{pct(stats.preventBasicAttackDamagedRatio)}</Descriptions.Item>
        <Descriptions.Item label="스킬 피해 감소">{pct(stats.preventSkillDamagedRatio)}</Descriptions.Item>
        <Descriptions.Item label="적응형 능력치">
          {num(stats.adaptiveForce, 0)}
          {stats.adaptiveForce > 0 && ` → ${stats.adaptiveAppliedTo === 'skill' ? '스킬 증폭' : '공격력'}`}
        </Descriptions.Item>
        <Descriptions.Item label="생명력 흡수">{pct(stats.lifeSteal)}</Descriptions.Item>
      </Descriptions>
    </Card>
  );
}

function DamageCard({ title, damage }) {
  if (!damage) {
    return null;
  }
  return (
    <Card size="small" title={title}>
      <Row gutter={[16, 16]}>
        <Col xs={12} md={6}><Statistic title="일반" value={damage.normal} precision={1} /></Col>
        <Col xs={12} md={6}><Statistic title="치명타" value={damage.critical} precision={1} /></Col>
        <Col xs={12} md={6}><Statistic title="기대값 (치명 확률 반영)" value={damage.expected} precision={1} /></Col>
        <Col xs={12} md={6}><Statistic title="처치까지 기대 타수" value={damage.hitsToKill ?? '-'} /></Col>
      </Row>
      <Typography.Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
        대상 유효 방어력 {num(damage.effectiveDefense)} → 방어력에 의한 피해 감소 {pct(damage.defenseReduction)}
      </Typography.Text>
    </Card>
  );
}

function PivotPanel({ pivot, onChange }) {
  const field = (key, label, { percent = false, max } = {}) => (
    <InputNumber style={{ width: '100%' }} addonBefore={label} addonAfter={percent ? '%' : undefined} min={0} max={max}
      value={percent ? Math.round((pivot[key] || 0) * 1000) / 10 : pivot[key]}
      onChange={(v) => onChange({ ...pivot, [key]: percent ? (v || 0) / 100 : (v || 0) })} />
  );
  return (
    <Card size="small" title="대상 (기준값)">
      <Space direction="vertical" style={{ width: '100%' }}>
        <Typography.Text type="secondary">단일 캐릭터 모드는 아래 기준 대상을 상대로 계산해요. 값은 바꿀 수 있어요.</Typography.Text>
        {field('maxHp', '체력')}
        {field('defense', '방어력')}
        {field('preventBasicAttackDamagedRatio', '기본 공격 피해 감소', { percent: true, max: 100 })}
        {field('preventBasicAttackDamaged', '기본 공격 피해 감소(고정)')}
        {field('preventCriticalStrikeDamaged', '치명타 피해 감소', { percent: true, max: 100 })}
      </Space>
    </Card>
  );
}

export default function DamageCalculator() {
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState(null);
  const [calcMode, setCalcMode] = useState('compare');
  const [gameMode, setGameMode] = useState('squad');
  const [grades, setGrades] = useState(DEFAULT_GRADES);
  const [attacker, setAttacker] = useState(emptyBuild());
  const [target, setTarget] = useState(emptyBuild());
  const [pivot, setPivot] = useState(DEFAULT_PIVOT);

  useEffect(() => {
    axios.get("/api/external/bser/damage-calculator/meta")
      .then((r) => setMeta({ ...r.data, items: excludeSkillBoundItems(r.data.items) }))
      .catch(() => setError('메타 데이터를 불러오지 못했어요. 잠시 후 다시 시도해 주세요.'));
  }, []);

  const statsOf = (build) => {
    const character = meta?.characters?.find((c) => c.code === build.characterCode);
    if (!character) return null;
    return {
      character,
      stats: computeStats({ ...build, character }),
      mode: modeModifier(character, build.weaponType, gameMode),
    };
  };

  const a = meta ? statsOf(attacker) : null;
  const t = meta && calcMode === 'compare' ? statsOf(target) : null;

  const zeroMode = { dealt: 0, taken: 0 };
  const forward = a && (calcMode === 'single'
    ? basicAttackDamage(a.stats, pivot, a.mode, zeroMode)
    : t && basicAttackDamage(a.stats, t.stats, a.mode, t.mode));
  const backward = a && t && calcMode === 'compare' ? basicAttackDamage(t.stats, a.stats, t.mode, a.mode) : null;

  if (error) {
    return <Alert type="error" message={error} />;
  }
  if (!meta) {
    return <div style={{ textAlign: 'center', padding: 48 }}><Spin /></div>;
  }

  return (
    <div>
      <Typography.Title level={4}>데미지 계산기</Typography.Title>
      <Space wrap style={{ marginBottom: 16 }}>
        <Segmented value={calcMode} onChange={setCalcMode}
          options={[{ label: '대상 비교', value: 'compare' }, { label: '단일 캐릭터', value: 'single' }]} />
        <Segmented value={gameMode} onChange={setGameMode} options={MODES.map((m) => ({ label: m.label, value: m.key }))} />
        <Select mode="multiple" style={{ minWidth: 220 }} value={grades} onChange={setGrades} placeholder="아이템 등급"
          options={GRADE_ORDER.map((g) => ({ value: g, label: GRADE_LABELS[g] }))} />
      </Space>

      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <BuildPanel title="공격자" meta={meta} build={attacker} onChange={setAttacker} grades={grades} />
        </Col>
        <Col xs={24} md={12}>
          {calcMode === 'compare'
            ? <BuildPanel title="대상" meta={meta} build={target} onChange={setTarget} grades={grades} />
            : <PivotPanel pivot={pivot} onChange={setPivot} />}
        </Col>
      </Row>

      <Divider orientation="left">기본 공격 피해</Divider>
      <Space direction="vertical" style={{ width: '100%' }}>
        {forward
          ? <DamageCard title={calcMode === 'compare' ? '공격자 → 대상' : '공격자 → 기준 대상'} damage={forward} />
          : <Empty description={calcMode === 'compare' ? '공격자와 대상을 선택하세요' : '공격자를 선택하세요'} />}
        {backward && <DamageCard title="대상 → 공격자" damage={backward} />}
      </Space>

      <Divider orientation="left">최종 스탯</Divider>
      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}><StatsCard title="공격자" stats={a?.stats} /></Col>
        {calcMode === 'compare' && <Col xs={24} md={12}><StatsCard title="대상" stats={t?.stats} /></Col>}
      </Row>

      <Divider orientation="left">스킬 피해</Divider>
      <Alert type="info" showIcon message="준비 중이에요"
        description="BSER API에 스킬 계수가 없어서, 스킬 데이터를 따로 모아 채워 나갈 예정이에요." />

      <Alert style={{ marginTop: 24 }} type="warning" showIcon message="계산 기준과 가정"
        description={(
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            <li>공식: 공식 위키(Damage) 기준. 기본 공격 = 공격력 × 100 / (100 + 유효 방어력) × 치명(+65% + 치명 피해 증가) × (1 + 기본 공격 증폭 − 대상 감소)</li>
            <li>적응형 능력치: 공격력/스킬 증폭 중 더 높은 쪽으로 자동 적용 (공격력 1:1, 스킬 증폭 1:2, 공식 위키 기준). 수동 지정도 가능해요.</li>
            <li>모드 보정(주는/받는 피해)은 캐릭터·무기별 BSER 메타 값을 계산 뒤에 곱해요.</li>
            <li>특성, 전술 스킬, 버프/디버프, 아이템 고유 효과는 포함하지 않아요. 인게임 수치와 다를 수 있어요.</li>
          </ul>
        )} />
    </div>
  );
}
